import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Event, EventType, CreateEventRequest, UpdateEventRequest, RecurrenceType, EventReminderInput } from '../../types/event';
import { Venue } from '../../types/venue';
import { eventsAPI, matchesAPI, venuesAPI } from '../../services/api';
import type { SelectablePlayer } from '../../types/match';
import AgeGroupSelect from '../common/AgeGroupSelect';
import PitchSectionSelector from '../venues/PitchSectionSelector';
import { parseVenueSections, sectionsLabel } from '../../utils/venueSections';
import { getErrorMessage } from '../../utils/errorMessage';
import { useClubSettings } from '../../contexts/ClubSettingsContext';
import { useToast } from '../../contexts/ToastContext';
import ReminderPicker from './ReminderPicker';
import MatchSquadPicker from './MatchSquadPicker';
import NewEventTravelFields, { DraftVehicle, buildTravelRequest } from '../travel/NewEventTravelFields';

interface EventFormProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event | null;
  onSave: () => void;
  selectedDate?: Date | null;
  defaultType?: EventType;
}

/** Ismétlődő eseményt legfeljebb ennyi hónapra előre lehet felvenni (a backend is ezt nézi). */
const MAX_RECURRENCE_MONTHS = 6;

const EventForm: React.FC<EventFormProps> = ({ isOpen, onClose, event, onSave, selectedDate, defaultType }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: EventType.Training,
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    location: '',
    ageGroupId: null as number | null,
    ageGroup: '', // Keep for backward compatibility
    // Korosztály helyett vendég csapat: a kettő kizárja egymást
    guestTeam: '',
    isRecurring: false,
    recurrenceType: RecurrenceType.Weekly,
    recurrenceInterval: 1,
    recurrenceEndDate: '',
    venueId: '',
    responsesEnabled: true,
    excuseLetterEnabled: false,
    notificationsEnabled: true,
    notifyOnCreate: false,
    notifyCreator: false,
  });
  // Az emlékeztetők külön állapotban vannak, mert nem egyszerű mezők
  const [reminders, setReminders] = useState<EventReminderInput[]>([]);
  const { settings: clubSettings } = useClubSettings();
  // Refben tartjuk, hogy a klubbeállítások frissülése ne nullázza a nyitott űrlapot
  const clubSettingsRef = useRef(clubSettings);
  clubSettingsRef.current = clubSettings;
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [venueConflict, setVenueConflict] = useState<string | null>(null);
  const [ageGroupConflict, setAgeGroupConflict] = useState<string | null>(null);
  // Korosztály helyett vendég csapat. Vendég csapatos eseményhez nincs
  // korosztály, ezért nem készül jelenléti lista sem - a válaszadás és az
  // igazoláskérés ilyenkor nem értelmezhető.
  const [useGuestTeam, setUseGuestTeam] = useState(false);
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  // Pályarész-foglalás: kijelölt részek (mind = egész pálya) + az idősávban foglalt részek
  const [selectedSections, setSelectedSections] = useState<number[]>([]);
  const [occupiedSections, setOccupiedSections] = useState<number[]>([]);
  const [fullyOccupied, setFullyOccupied] = useState(false);
  // Mérkőzésnél: futsal, és (létrehozáskor) opcionálisan a keret
  const [isFutsal, setIsFutsal] = useState(false);
  // Szerkesztésnél csak a betöltött értéket küldjük vissza, nehogy a régit felülírjuk
  const [futsalKnown, setFutsalKnown] = useState(true);
  const [squadEnabled, setSquadEnabled] = useState(false);
  const [squadIds, setSquadIds] = useState<Set<number>>(new Set());
  const [selectablePlayers, setSelectablePlayers] = useState<SelectablePlayer[]>([]);
  const [ageGroupName, setAgeGroupName] = useState('');
  const isMatch = Number(formData.type) === EventType.Match;
  // Utazás az új eseménnyel együtt
  const [travelEnabled, setTravelEnabled] = useState(false);
  const [travelVehicles, setTravelVehicles] = useState<DraftVehicle[]>([]);
  // Több napos esemény: csak ekkor kell külön befejező dátum
  const [multiDay, setMultiDay] = useState(false);
  // Ritkábban kellő beállítások (visszajelzés, kikérő, értesítések)
  const [showMore, setShowMore] = useState(false);

  const selectedVenue = venues.find(
    (v) => v.id === (formData.venueId ? parseInt(formData.venueId) : -1)
  );
  const sectionCount = selectedVenue?.sections ?? 1;

  // Load all events for conflict checking (without role filtering)
  const loadAllEvents = async () => {
    try {
      const events = await eventsAPI.getAllForConflictCheck();
      setAllEvents(events);
    } catch (err) {
      console.error('Error loading events for conflict check:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadVenues();
      loadAllEvents();
      if (event) {
        const startDate = new Date(event.startTime);
        const endDate = new Date(event.endTime);
        setFormData({
          title: event.title,
          description: event.description || '',
          type: event.type,
          startDate: startDate.toISOString().split('T')[0],
          startTime: startDate.toTimeString().slice(0, 5),
          endDate: endDate.toISOString().split('T')[0],
          endTime: endDate.toTimeString().slice(0, 5),
          location: event.location || '',
          ageGroupId: event.ageGroupId || null,
          ageGroup: event.ageGroup,
          guestTeam: event.guestTeam || '',
          isRecurring: event.isRecurring,
          recurrenceType: event.recurrenceType || RecurrenceType.Weekly,
          recurrenceInterval: event.recurrenceInterval || 1,
          recurrenceEndDate: event.recurrenceEndDate
            ? new Date(event.recurrenceEndDate).toISOString().split('T')[0]
            : '',
          venueId: event.venueId?.toString() || '',
          responsesEnabled: event.responsesEnabled,
          excuseLetterEnabled: event.excuseLetterEnabled || false,
          notificationsEnabled: event.notificationsEnabled ?? true,
          notifyOnCreate: event.notifyOnCreate ?? false,
          notifyCreator: event.notifyCreator ?? false,
        });
        setReminders(
          (event.reminders ?? []).map((r) => ({
            offsetMinutes: r.offsetMinutes,
            daysBefore: r.daysBefore,
            timeOfDayMinutes: r.timeOfDayMinutes,
          }))
        );
      } else {
        // Use selectedDate if provided, otherwise use current date/time
        const baseDate = selectedDate || new Date();
        const startHour = selectedDate ? baseDate.getHours() : 10;
        const startMinute = selectedDate ? baseDate.getMinutes() : 0;

        // Format date in local timezone (not UTC) to avoid day shift
        const formatLocalDate = (date: Date) => {
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const day = date.getDate().toString().padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        setFormData({
          title: '',
          description: '',
          type: defaultType ?? EventType.Training,
          startDate: formatLocalDate(baseDate),
          startTime: `${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`,
          endDate: formatLocalDate(baseDate),
          endTime: `${(startHour + 1).toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`,
          location: '',
          ageGroupId: null,
          ageGroup: '',
          guestTeam: '',
          isRecurring: false,
          recurrenceType: RecurrenceType.Weekly,
          recurrenceInterval: 1,
          recurrenceEndDate: '',
          venueId: '',
          responsesEnabled: true,
          excuseLetterEnabled: false,
          notificationsEnabled: true,
          notifyOnCreate: clubSettingsRef.current?.notifyOnEventCreateByDefault ?? false,
          notifyCreator: false,
        });
        // Új eseménynél a klub alapértelmezett emlékeztetőivel indulunk
        setReminders(clubSettingsRef.current?.defaultEventReminders ?? []);
      }
      setUseGuestTeam(!!event?.guestTeam);
      setAgeGroupName(event?.ageGroup ?? '');
      setTravelEnabled(false);
      setTravelVehicles([]);
      setMultiDay(!!event && new Date(event.startTime).toDateString() !== new Date(event.endTime).toDateString());
      setShowMore(false);
      setIsFutsal(false);
      setSquadEnabled(false);
      setSquadIds(new Set());
      // Szerkesztésnél a futsal jelölés a mérkőzés adataiból jön
      const hasMatch = !!event && event.type === EventType.Match;
      setFutsalKnown(!hasMatch);
      if (hasMatch) {
        eventsAPI
          .getMatch(event!.id)
          .then((match) => {
            setIsFutsal(!!match.isFutsal);
            setFutsalKnown(true);
          })
          .catch(() => undefined);
      }
      setError(null);
      setVenueConflict(null);
      setAgeGroupConflict(null);
    }
  }, [isOpen, event, selectedDate, defaultType]);

  // A keret választásához a játékosok (csak új mérkőzésnél kell)
  useEffect(() => {
    if (!isOpen || event || !isMatch || selectablePlayers.length > 0) return;
    matchesAPI
      .getSelectablePlayers()
      .then(setSelectablePlayers)
      .catch((err) => console.error('A játékosok betöltése nem sikerült:', err));
  }, [isOpen, event, isMatch, selectablePlayers.length]);

  const squadCandidates = selectablePlayers.filter((p) => ageGroupName && p.ageGroups.includes(ageGroupName));

  const loadVenues = async () => {
    try {
      const data = await venuesAPI.getAllVenues();
      setVenues(data);
    } catch (err) {
      console.error('Error loading venues:', err);
    }
  };

  // Pálya (vagy megnyitás) változásakor a kijelölés alapállapota:
  // szerkesztésnél a mentett részek, egyébként az egész pálya
  useEffect(() => {
    if (!isOpen) return;
    const venue = venues.find((v) => v.id === (formData.venueId ? parseInt(formData.venueId) : -1));
    const count = venue?.sections ?? 1;
    if (!venue || count <= 1) {
      setSelectedSections([]);
      return;
    }
    const saved =
      event && event.venueId === venue.id
        ? (parseVenueSections(event.venueSections) || []).filter((n) => n <= count)
        : [];
    setSelectedSections(saved.length > 0 ? saved : Array.from({ length: count }, (_, i) => i + 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, formData.venueId, venues, event?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Send datetime without UTC conversion to preserve local time. Egynapos
    // eseménynél a befejezés ugyanarra a napra esik.
    const endDate = multiDay ? formData.endDate : formData.startDate;
    const startTime = `${formData.startDate}T${formData.startTime}:00`;
    const endTime = `${endDate}T${formData.endTime}:00`;
    if (new Date(endTime) <= new Date(startTime)) {
      setError(
        multiDay
          ? 'A befejezés legyen a kezdés után.'
          : 'A befejezés legyen a kezdés után. Ha az esemény átnyúlik a következő napra, kapcsold be a „Több napos” lehetőséget.'
      );
      setLoading(false);
      return;
    }

    // Szerkesztéskor az üres mezőt is el kell küldeni (üres string, pálya
    // esetén 0), különben a backend "nem változott"-nak veszi, és a korábbi
    // érték megmarad - így nem lehetne törölni a kézzel beírt helyszínt.
    const cleared = <T,>(value: T) => (event ? value : undefined);

    const eventData: CreateEventRequest | UpdateEventRequest = {
        title: formData.title,
        description: formData.description.trim() || cleared(''),
        type: Number(formData.type),
        startTime,
        endTime,
        location: formData.location.trim() || cleared(''),
        // A backend a kettő közül pontosan az egyiket várja. Az üres string a
        // vendég csapatnál azt jelenti: váltsunk vissza korosztályra.
        ageGroup: useGuestTeam ? '' : formData.ageGroup,
        ageGroupId: useGuestTeam ? undefined : formData.ageGroupId || undefined,
        guestTeam: useGuestTeam ? formData.guestTeam.trim() : '',
        isRecurring: formData.isRecurring,
        recurrenceType: formData.isRecurring ? Number(formData.recurrenceType) : undefined,
        recurrenceInterval: formData.isRecurring ? Number(formData.recurrenceInterval) || 1 : undefined,
        recurrenceEndDate: formData.isRecurring && formData.recurrenceEndDate
          ? `${formData.recurrenceEndDate}T23:59:59`
          : undefined,
        venueId: formData.venueId ? parseInt(formData.venueId) : cleared(0),
        // részleges kijelölés -> CSV; egész pálya -> üres string (a backend nullázza)
        venueSections: formData.venueId
          ? selectedSections.length > 0 && selectedSections.length < sectionCount
            ? selectedSections.join(',')
            : ''
          : undefined,
        responsesEnabled: useGuestTeam ? false : formData.responsesEnabled,
        excuseLetterEnabled: useGuestTeam ? false : formData.excuseLetterEnabled,
        notificationsEnabled: formData.notificationsEnabled,
        notifyOnCreate: formData.notificationsEnabled && formData.notifyOnCreate,
        notifyCreator: formData.notificationsEnabled && formData.notifyCreator,
        // Kikapcsolt értesítésnél nem tartunk fenn emlékeztetőket
        reminders: formData.notificationsEnabled ? reminders : [],
        ...(isMatch && futsalKnown ? { isFutsal } : {}),
      };

    // A keret csak új, korosztályos mérkőzésnél adható meg (később a mérkőzés lapján módosítható)
    if (!event && isMatch && !useGuestTeam && squadEnabled) {
      const ids = squadCandidates.filter((p) => squadIds.has(p.id)).map((p) => p.id);
      if (ids.length === 0) {
        setError('Jelölj ki legalább egy játékost a keretbe, vagy kapcsold ki a keretet.');
        setLoading(false);
        return;
      }
      (eventData as CreateEventRequest).squadPlayerIds = ids;
    }

    // Utazás csak új eseménynél, a megadott járművekkel és megállókkal
    if (!event && travelEnabled) {
      if (travelVehicles.length === 0) {
        setError('Adj hozzá legalább egy járművet az utazáshoz, vagy kapcsold ki az utazást.');
        setLoading(false);
        return;
      }
      (eventData as CreateEventRequest).travel = buildTravelRequest(travelVehicles, formData.startDate);
    }

    try {
      if (event) {
        // Ha a típus mérkőzésről másra vált, rákérdezünk a mérkőzés adatok törlésére
        if (event.type === EventType.Match && Number(formData.type) !== EventType.Match) {
          (eventData as UpdateEventRequest).deleteMatch = window.confirm(
            'Az esemény típusa már nem mérkőzés. Töröljük a hozzá tartozó mérkőzés adatokat is (keret, kezdőcsapat, események)?\n\nOK = törlés, Mégse = megtartás',
          );
        }
        await eventsAPI.update(event.id, eventData as UpdateEventRequest);
      } else {
        await eventsAPI.create(eventData as CreateEventRequest);
      }

      showToast(event ? 'Esemény mentve.' : 'Esemény létrehozva.', 'success');
      onSave();
    } catch (err: any) {
      console.error('Event save error:', err.response?.status, err.response?.data, eventData);
      setError(getErrorMessage(err, 'Hiba történt a mentés során'));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to add hours to a time string (HH:mm format)
  const addHoursToTime = (timeStr: string, hours: number): string => {
    const [h, m] = timeStr.split(':').map(Number);
    const totalMinutes = h * 60 + m + hours * 60;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
  };

  // Check if start datetime is after end datetime
  const isStartAfterEnd = (startDate: string, startTime: string, endDate: string, endTime: string): boolean => {
    if (!startDate || !startTime || !endDate || !endTime) return false;
    const start = new Date(`${startDate}T${startTime}`);
    const end = new Date(`${endDate}T${endTime}`);
    return start >= end;
  };

  // Check for conflicts using useEffect
  useEffect(() => {
    if (!isOpen || allEvents.length === 0) return;
    if (!formData.startDate || !formData.startTime || !formData.endDate || !formData.endTime) return;

    const newStart = new Date(`${formData.startDate}T${formData.startTime}`);
    const newEnd = new Date(`${formData.endDate}T${formData.endTime}`);

    // Filter out current event if editing
    const otherEvents = allEvents.filter(e => e.id !== event?.id);

    // Check venue conflict (pályarész-tudatos)
    if (formData.venueId) {
      const venueId = parseInt(formData.venueId);
      const venue = venues.find((v) => v.id === venueId);
      const count = venue?.sections ?? 1;
      // a saját foglalt részeink; null = egész pálya
      const mySections =
        selectedSections.length > 0 && selectedSections.length < count ? selectedSections : null;

      // Az idősávban átfedő események ezen a pályán
      const overlapping = otherEvents.filter(e => {
        if (e.venueId !== venueId) return false;
        const eventStart = new Date(e.startTime);
        const eventEnd = new Date(e.endTime);
        return eventStart < newEnd && eventEnd > newStart;
      });

      // A pályaválasztón jelölendő foglalt részek
      const occupied = new Set<number>();
      let hasFull = false;
      overlapping.forEach((e) => {
        const theirs = parseVenueSections(e.venueSections);
        if (theirs === null) hasFull = true;
        else theirs.forEach((s) => occupied.add(s));
      });
      setOccupiedSections(Array.from(occupied).sort((a, b) => a - b));
      setFullyOccupied(hasFull);

      // Tényleges ütközés: csak ha a részek metszik egymást
      const venueEvents = overlapping.filter((e) => {
        const theirs = parseVenueSections(e.venueSections);
        if (mySections === null || theirs === null) return true;
        return theirs.some((s) => mySections.includes(s));
      });
      if (venueEvents.length > 0) {
        const conflictEvent = venueEvents[0];
        const startTime = new Date(conflictEvent.startTime).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
        const endTime = new Date(conflictEvent.endTime).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
        const ageGroup = conflictEvent.ageGroup ? ` - ${conflictEvent.ageGroup}` : '';
        const theirSections = parseVenueSections(conflictEvent.venueSections);
        const sectionInfo = theirSections ? `, ${sectionsLabel(theirSections, venue?.sectionNames)}` : '';
        setVenueConflict(`Ütközés: ${conflictEvent.title}${ageGroup} (${startTime} - ${endTime}${sectionInfo})`);
      } else {
        setVenueConflict(null);
      }
    } else {
      setVenueConflict(null);
      setOccupiedSections([]);
      setFullyOccupied(false);
    }

    // Check age group conflict
    if (formData.ageGroupId && !useGuestTeam) {
      const ageGroupEvents = otherEvents.filter(e => {
        if (e.ageGroupId !== formData.ageGroupId) return false;
        const eventStart = new Date(e.startTime);
        const eventEnd = new Date(e.endTime);
        // Check for overlap
        return eventStart < newEnd && eventEnd > newStart;
      });
      if (ageGroupEvents.length > 0) {
        const conflictEvent = ageGroupEvents[0];
        const startTime = new Date(conflictEvent.startTime).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
        const endTime = new Date(conflictEvent.endTime).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
        const ageGroup = conflictEvent.ageGroup ? ` - ${conflictEvent.ageGroup}` : '';
        setAgeGroupConflict(`Ütközés: ${conflictEvent.title}${ageGroup} (${startTime} - ${endTime})`);
      } else {
        setAgeGroupConflict(null);
      }
    } else {
      setAgeGroupConflict(null);
    }
  }, [isOpen, allEvents, venues, selectedSections, formData.startDate, formData.startTime, formData.endDate, formData.endTime, formData.venueId, formData.ageGroupId, useGuestTeam, event?.id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      // Egynapos eseménynél a befejező dátum mindig a kezdőnap
      if (name === 'startDate' && !multiDay) {
        newData.endDate = newData.startDate;
      }

      // Auto-adjust end time if start is after end
      if (name === 'startDate' || name === 'startTime') {
        if (isStartAfterEnd(newData.startDate, newData.startTime, newData.endDate, newData.endTime)) {
          newData.endDate = newData.startDate;
          newData.endTime = addHoursToTime(newData.startTime, 1);
        }
      }

      return newData;
    });
  };

  const handleAgeGroupChange = (ageGroupId: number | null, name?: string) => {
    setFormData((prev) => ({
      ...prev,
      ageGroupId,
    }));
    setAgeGroupName(name ?? '');
  };

  // Ismétlődésnél: meddig lehet, és hány alkalom jön létre (a backend ugyanígy számol)
  const maxRecurrenceEnd = (() => {
    if (!formData.startDate) return undefined;
    const d = new Date(`${formData.startDate}T00:00:00`);
    d.setMonth(d.getMonth() + MAX_RECURRENCE_MONTHS);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const occurrenceCount = (() => {
    if (!formData.isRecurring || !formData.startDate || !formData.recurrenceEndDate) return 0;
    const start = new Date(`${formData.startDate}T00:00:00`);
    const last = new Date(`${formData.recurrenceEndDate}T00:00:00`);
    const interval = Math.max(1, Number(formData.recurrenceInterval) || 1);
    let count = 0;
    for (let i = 0; i < 500; i++) {
      const d = new Date(start);
      const type = Number(formData.recurrenceType);
      if (type === RecurrenceType.Daily) d.setDate(d.getDate() + i * interval);
      else if (type === RecurrenceType.Weekly) d.setDate(d.getDate() + 7 * i * interval);
      else d.setMonth(d.getMonth() + i * interval);
      if (d > last) break;
      count++;
    }
    return count;
  })();

  // Egységes mezőstílus: mobilon 16px, különben az iOS ránagyít a mezőre
  const inputClass =
    'w-full rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
  const labelClass = 'mb-1 block text-sm font-medium text-gray-700';

  const typeOptions: { value: EventType; label: string }[] = [
    { value: EventType.Training, label: 'Edzés' },
    { value: EventType.Match, label: 'Mérkőzés' },
    { value: EventType.Meeting, label: 'Megbeszélés' },
    { value: EventType.Other, label: 'Egyéb' },
  ];
  const currentType = Number(formData.type) as EventType;
  const typeWord: Record<EventType, string> = {
    [EventType.Training]: 'edzés',
    [EventType.Match]: 'mérkőzés',
    [EventType.Meeting]: 'megbeszélés',
    [EventType.Other]: 'program',
  };
  // Névjavaslat, amíg nincs beírva semmi: "U11 edzés"
  const team = useGuestTeam ? formData.guestTeam.trim() : ageGroupName;
  const titleSuggestion = team ? `${team} ${typeWord[currentType]}` : '';

  /** A kezdéshez képest ennyi perc múlva ér véget (gyors gombokhoz). */
  const setDuration = (minutes: number) => {
    if (!formData.startTime) return;
    const [h, m] = formData.startTime.split(':').map(Number);
    const total = h * 60 + m + minutes;
    const endTime = `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
    setFormData((prev) => ({ ...prev, endTime }));
  };
  const durationMinutes = (() => {
    if (multiDay || !formData.startTime || !formData.endTime) return null;
    const [sh, sm] = formData.startTime.split(':').map(Number);
    const [eh, em] = formData.endTime.split(':').map(Number);
    return eh * 60 + em - (sh * 60 + sm);
  })();

  const section = (title: string, children: React.ReactNode, hint?: string) => (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
      </div>
      {children}
    </section>
  );

  const checkbox = (name: string, checked: boolean, label: React.ReactNode, description?: string) => (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg px-1 py-1.5">
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={handleChange}
        className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <span>
        <span className="block text-sm font-medium text-gray-800">{label}</span>
        {description && <span className="block text-xs text-gray-500">{description}</span>}
      </span>
    </label>
  );

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex min-h-full items-stretch justify-center md:items-center md:p-4">
        <Dialog.Panel className="modal-panel-mobile flex w-full flex-col bg-white shadow-xl md:max-h-[92vh] md:max-w-2xl md:rounded-lg">
          <div className="flex shrink-0 items-center justify-between border-b px-4 py-3 md:px-6 md:py-4">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {event ? 'Esemény szerkesztése' : 'Új esemény'}
            </Dialog.Title>
            <button
              type="button"
              onClick={onClose}
              aria-label="Bezárás"
              className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-7 overflow-y-auto px-4 py-4 md:px-6 md:py-5">
              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
                  {error}
                </div>
              )}

              {/* 1. Mi lesz? */}
              {section(
                'Mi lesz?',
                <>
                  <div className="grid grid-cols-2 gap-1.5 rounded-lg bg-gray-100 p-1 sm:grid-cols-4" role="radiogroup" aria-label="Típus">
                    {typeOptions.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        role="radio"
                        aria-checked={currentType === t.value}
                        onClick={() => setFormData((prev) => ({ ...prev, type: t.value }))}
                        className={`rounded-md px-2 py-2 text-sm font-medium transition-colors ${
                          currentType === t.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>

                  <div>
                    <label htmlFor="title" className={labelClass}>
                      Név *
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      required
                      value={formData.title}
                      onChange={handleChange}
                      placeholder={titleSuggestion || 'Pl. U11 edzés'}
                      className={inputClass}
                    />
                    {!formData.title && titleSuggestion && (
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, title: titleSuggestion }))}
                        className="mt-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100"
                      >
                        Legyen „{titleSuggestion}”
                      </button>
                    )}
                  </div>

                  <div>
                    <label htmlFor="description" className={labelClass}>
                      Leírás <span className="font-normal text-gray-400">(nem kötelező)</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={2}
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Pl. sípcsontvédő kötelező, gyülekező 15 perccel előtte"
                      className={inputClass}
                    />
                  </div>
                </>
              )}

              {/* 2. Kiknek? */}
              {section(
                'Kiknek?',
                <>
                  <div className="inline-flex rounded-lg bg-gray-100 p-1">
                    {[
                      { guest: false, label: 'Korosztály' },
                      { guest: true, label: 'Vendég csapat' },
                    ].map((o) => (
                      <button
                        key={o.label}
                        type="button"
                        onClick={() => setUseGuestTeam(o.guest)}
                        aria-pressed={useGuestTeam === o.guest}
                        className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                          useGuestTeam === o.guest ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>

                  {useGuestTeam ? (
                    <div>
                      <label htmlFor="guestTeam" className={labelClass}>
                        Vendég csapat *
                      </label>
                      <input
                        type="text"
                        id="guestTeam"
                        name="guestTeam"
                        required
                        maxLength={100}
                        value={formData.guestTeam}
                        onChange={handleChange}
                        placeholder="Pl. Szomszédvár FC"
                        className={inputClass}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Vendég csapatos eseményt csak a stáb lát, és nem tartozik hozzá jelenléti lista.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <label htmlFor="ageGroup" className={labelClass}>
                        Korosztály *
                      </label>
                      <AgeGroupSelect
                        value={formData.ageGroupId}
                        onChange={handleAgeGroupChange}
                        required
                        autoSelectSingle={true}
                        className={`${inputClass} ${ageGroupConflict ? 'border-orange-400' : ''}`}
                      />
                      {ageGroupConflict && <p className="mt-1 text-sm text-orange-600">{ageGroupConflict}</p>}
                    </div>
                  )}

                  {/* Mérkőzés: futsal, és létrehozáskor az opcionális keret */}
                  {isMatch && (
                    <div className="space-y-2 rounded-lg border border-red-100 bg-red-50/40 p-3">
                      <label className="flex cursor-pointer items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isFutsal}
                          disabled={!futsalKnown}
                          onChange={(e) => setIsFutsal(e.target.checked)}
                          className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-800">
                          <span className="font-medium">Futsal</span>
                          <span className="text-gray-500"> – a felállás 4+1 fős</span>
                        </span>
                      </label>

                      {!event && !useGuestTeam && (
                        <div>
                          <label className="flex cursor-pointer items-start gap-3">
                            <input
                              type="checkbox"
                              checked={squadEnabled}
                              onChange={(e) => setSquadEnabled(e.target.checked)}
                              className="mt-0.5 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span>
                              <span className="block text-sm font-medium text-gray-800">
                                Keret megadása <span className="font-normal text-gray-500">(nem kötelező)</span>
                              </span>
                              <span className="block text-xs text-gray-500">
                                A meccset és az értesítést a teljes korosztály megkapja, de visszajelezni és az utazást látni
                                csak a keret tagjai tudnak. Később a mérkőzés lapján módosítható.
                              </span>
                            </span>
                          </label>
                          {squadEnabled && (
                            <div className="mt-2">
                              {ageGroupName ? (
                                <MatchSquadPicker players={squadCandidates} selected={squadIds} onChange={setSquadIds} />
                              ) : (
                                <p className="text-sm text-gray-500">Előbb válaszd ki a korosztályt.</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* 3. Mikor? */}
              {section(
                'Mikor?',
                <>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-[1.4fr_1fr_1fr]">
                    <div className="col-span-2 sm:col-span-1">
                      <label htmlFor="startDate" className={labelClass}>
                        {multiDay ? 'Kezdő nap *' : 'Nap *'}
                      </label>
                      <input
                        type="date"
                        id="startDate"
                        name="startDate"
                        required
                        value={formData.startDate}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="startTime" className={labelClass}>
                        Kezdés *
                      </label>
                      <input
                        type="time"
                        id="startTime"
                        name="startTime"
                        required
                        value={formData.startTime}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="endTime" className={labelClass}>
                        Vége *
                      </label>
                      <input
                        type="time"
                        id="endTime"
                        name="endTime"
                        required
                        value={formData.endTime}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    </div>
                    {multiDay && (
                      <div className="col-span-2 sm:col-span-1">
                        <label htmlFor="endDate" className={labelClass}>
                          Befejező nap *
                        </label>
                        <input
                          type="date"
                          id="endDate"
                          name="endDate"
                          required
                          min={formData.startDate}
                          value={formData.endDate}
                          onChange={handleChange}
                          className={inputClass}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {!multiDay &&
                      [60, 90, 120].map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setDuration(m)}
                          aria-pressed={durationMinutes === m}
                          className={`rounded-full px-3 py-1 text-xs font-medium ${
                            durationMinutes === m
                              ? 'bg-primary-600 text-white'
                              : 'bg-white text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {m === 60 ? '1 óra' : m === 90 ? '1,5 óra' : '2 óra'}
                        </button>
                      ))}
                    <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={multiDay}
                        onChange={(e) => {
                          setMultiDay(e.target.checked);
                          if (!e.target.checked) setFormData((prev) => ({ ...prev, endDate: prev.startDate }));
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Több napos
                    </label>
                  </div>

                  {/* Ismétlődés: csak új eseménynél - minden alkalom külön esemény lesz,
                      szerkesztéskor egy alkalmat módosítunk */}
                  {!event && (
                    <div className="rounded-lg border border-gray-200 p-3">
                      {checkbox('isRecurring', formData.isRecurring, 'Ismétlődik', 'Pl. heti edzés: minden alkalom külön esemény lesz, saját visszajelzéssel.')}
                      {formData.isRecurring && (
                        <div className="mt-2 grid grid-cols-2 gap-3">
                          <div>
                            <label htmlFor="recurrenceType" className={labelClass}>
                              Milyen gyakran?
                            </label>
                            <select
                              id="recurrenceType"
                              name="recurrenceType"
                              value={formData.recurrenceType}
                              onChange={handleChange}
                              className={inputClass}
                            >
                              <option value={RecurrenceType.Daily}>Naponta</option>
                              <option value={RecurrenceType.Weekly}>Hetente</option>
                              <option value={RecurrenceType.Monthly}>Havonta</option>
                            </select>
                          </div>
                          <div>
                            <label htmlFor="recurrenceInterval" className={labelClass}>
                              Hányanként?
                            </label>
                            <input
                              type="number"
                              id="recurrenceInterval"
                              name="recurrenceInterval"
                              min="1"
                              value={formData.recurrenceInterval}
                              onChange={handleChange}
                              className={inputClass}
                            />
                          </div>
                          <div className="col-span-2">
                            <label htmlFor="recurrenceEndDate" className={labelClass}>
                              Meddig? *
                            </label>
                            <input
                              type="date"
                              id="recurrenceEndDate"
                              name="recurrenceEndDate"
                              required
                              min={formData.startDate}
                              max={maxRecurrenceEnd}
                              value={formData.recurrenceEndDate}
                              onChange={handleChange}
                              className={inputClass}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              {occurrenceCount > 0
                                ? `${occurrenceCount} alkalom jön létre. Legfeljebb ${MAX_RECURRENCE_MONTHS} hónapra előre.`
                                : `Legfeljebb ${MAX_RECURRENCE_MONTHS} hónapra előre vehetsz fel ismétlődő eseményt.`}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* 4. Hol? */}
              {section(
                'Hol?',
                <>
                  <div>
                    <label htmlFor="venueId" className={labelClass}>
                      Pálya
                    </label>
                    <select
                      id="venueId"
                      name="venueId"
                      value={formData.venueId}
                      onChange={handleChange}
                      className={`${inputClass} ${venueConflict ? 'border-orange-400' : ''}`}
                    >
                      <option value="">Nem a klub pályáján</option>
                      {venues.map((venue) => (
                        <option key={venue.id} value={venue.id}>
                          {venue.name}
                        </option>
                      ))}
                    </select>
                    {venueConflict && <p className="mt-1 text-sm text-orange-600">{venueConflict}</p>}
                  </div>

                  {/* Pályarész-választó: negyed/fél/egész pálya kijelölése kattintással */}
                  {sectionCount > 1 && (
                    <PitchSectionSelector
                      sectionCount={sectionCount}
                      selected={selectedSections}
                      onChange={setSelectedSections}
                      occupiedSections={occupiedSections}
                      fullyOccupied={fullyOccupied}
                      sectionNames={selectedVenue?.sectionNames}
                    />
                  )}

                  {(!formData.venueId || formData.location) && (
                    <div>
                      <label htmlFor="location" className={labelClass}>
                        Helyszín
                      </label>
                      <input
                        type="text"
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="Pl. idegenbeli pálya címe"
                        className={inputClass}
                      />
                    </div>
                  )}
                </>
              )}

              {/* 5. Utazás - csak új eseménynél; utána az esemény "Utazás szerkesztése" gombjával */}
              {!event &&
                section(
                  'Utazás',
                  <>
                    <label className="flex cursor-pointer items-start gap-3 rounded-lg px-1 py-1.5">
                      <input
                        type="checkbox"
                        checked={travelEnabled}
                        onChange={(e) => setTravelEnabled(e.target.checked)}
                        className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-800">Utazást is szervezünk</span>
                        <span className="block text-xs text-gray-500">
                          Járművek és megállók most; ki kivel megy, azt később osztod be.
                          {formData.isRecurring && ' Ismétlődésnél minden alkalomra ugyanígy.'}
                        </span>
                      </span>
                    </label>
                    {travelEnabled && (
                      <NewEventTravelFields
                        vehicles={travelVehicles}
                        onChange={setTravelVehicles}
                        startTime={formData.startTime}
                      />
                    )}
                  </>
                )}

              {/* 6. További beállítások - ritkábban kell, alapból összecsukva */}
              <section className="rounded-lg border border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowMore((v) => !v)}
                  aria-expanded={showMore}
                  className="flex w-full items-center justify-between px-3 py-3 text-left"
                >
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">Visszajelzés és értesítések</span>
                    <span className="block text-xs text-gray-500">
                      {[
                        !useGuestTeam && formData.responsesEnabled ? 'visszajelzés be' : 'visszajelzés ki',
                        formData.notificationsEnabled ? `${reminders.length} emlékeztető` : 'értesítés ki',
                        !useGuestTeam && formData.excuseLetterEnabled ? 'kikérő kérhető' : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${showMore ? 'rotate-180' : ''}`} />
                </button>

                {showMore && (
                  <div className="space-y-3 border-t border-gray-200 px-3 py-3">
                    {/* Visszajelzés és kikérő: vendég csapatos eseményhez nincs
                        játékoslista, így ezeknek nem lenne kit kérdezniük */}
                    {!useGuestTeam && (
                      <>
                        {checkbox('responsesEnabled', formData.responsesEnabled, 'Visszajelzés', 'A játékosok és a szülők jelzik, hogy jönnek-e.')}
                        {checkbox('excuseLetterEnabled', formData.excuseLetterEnabled, 'Kikérő kérhető', 'A szülő az iskolába szóló kikérőt kérhet.')}
                      </>
                    )}

                    {checkbox(
                      'notificationsEnabled',
                      formData.notificationsEnabled,
                      'Értesítés küldése az eseményről',
                      formData.notificationsEnabled
                        ? 'A korosztály játékosai, szüleik és az edzők push értesítést kapnak. Az elmaradásról és a változásról mindig szólunk.'
                        : 'Erről az eseményről semmilyen push értesítés nem megy ki.'
                    )}

                    {formData.notificationsEnabled && (
                      <div className="space-y-2 pl-8">
                        <div>
                          <div className="mb-2 text-sm font-medium text-gray-700">Emlékeztetők</div>
                          <ReminderPicker
                            value={reminders}
                            onChange={setReminders}
                            eventStart={
                              formData.startDate && formData.startTime ? `${formData.startDate}T${formData.startTime}:00` : null
                            }
                            savedReminders={event?.reminders}
                          />
                        </div>
                        {checkbox('notifyOnCreate', formData.notifyOnCreate, 'Azonnali értesítés a felvételkor')}
                        {checkbox('notifyCreator', formData.notifyCreator, 'Nekem is küldd el', 'Alapból a létrehozó nem kap értesítést a saját eseményéről.')}
                      </div>
                    )}
                  </div>
                )}
              </section>
            </div>

            {/* Gombok: mobilon is mindig látszanak, nem kell legörgetni hozzájuk */}
            <div className="flex shrink-0 gap-2 border-t bg-white px-4 py-3 pb-safe-3 md:justify-end md:px-6">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-md bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 md:flex-none"
              >
                Mégse
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-[2] rounded-md bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50 md:flex-none"
              >
                {loading
                  ? 'Mentés...'
                  : event
                    ? 'Mentés'
                    : formData.isRecurring && occurrenceCount > 1
                      ? `${occurrenceCount} alkalom létrehozása`
                      : 'Létrehozás'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default EventForm;
